import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  bufferTime,
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  CalendarEvent,
  Company,
  ConstructionSite,
  WorkDay,
  User,
  ConstructionDayReport,
  TimeEntry,
  UserType,
  UserRegistration,
  Calendar,
  WorkingOverviewRequest,
  NodeItem,
  AbsenceEntry,
  WorkingEntry,
  Machine,
  Inspection,
  ConstructionSiteState,
  AuthResult,
  ConstructionDayReportType,
  AbsenceEntryRequest,
  MissingWorkEntry,
  ReportCreationResult,
} from '../api/models';
import { Preferences } from '@capacitor/preferences';
import {
  AlertController,
  ModalController,
  Platform,
  PopoverController,
} from '@ionic/angular';
import { RouterService } from './router.service';
import { ToolService } from './tool.service';
import {
  ApiConfiguration,
  ConstructionDayReportDetailComponent,
  ConstructionDayReportFormComponent,
  ContextMenuComponent,
  ViewerDialogComponent,
} from '../public-api';
import { ContextMenuOption } from '../lib/models/context-menu-option';
import { AbsenceEntryService } from '../api/services/absence-entry.service';
import {
  UserService,
  ConstructionSiteService,
  ConfigurationService,
  WorkingDayService,
  CalendarEventService,
  CalendarService,
  ConstructionDayReportService,
  CompanyService,
  AuthService,
  BinaryService,
  NodeItemService,
  MachineService,
  InspectionService,
  MissingWorkEntryService,
} from '../api/services';
import * as signalR from '@microsoft/signalr';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Router } from '@angular/router';
import { UploadDocumentRequest } from '../lib/models/upload-document-reqest';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalRole } from '../lib/models/modal-roles';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  constructor(
    private userService: UserService,
    private conSiteService: ConstructionSiteService,
    private absenceEntryService: AbsenceEntryService,
    private configService: ConfigurationService,
    private workDayService: WorkingDayService,
    private calendarEventService: CalendarEventService,
    private calendarService: CalendarService,
    private constructionDayReportService: ConstructionDayReportService,
    private companyService: CompanyService,
    private authService: AuthService,
    private routerService: RouterService,
    private toolService: ToolService,
    private binaryService: BinaryService,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private nodeItemService: NodeItemService,
    private platform: Platform,
    private modalController: ModalController,
    private machineService: MachineService,
    private inspectionService: InspectionService,
    private base: ApiConfiguration,
    private router: Router,
    private missingWorkEntryService: MissingWorkEntryService
  ) {}

  public version: number = 5.5;
  public isMobile: boolean = false; // mobile project
  public isBigScreen: boolean = false; // desktop or tablet
  public isIOS: boolean = false; // ios
  public USERNAMEKEY = 'USERNAME';
  public STAYLOGGEDINKEY = 'STAYLOGGEDIN';
  public REFRESHTOKENKEY = 'REFRESHTOKEN';
  public ACCESSTOKENKEY = 'ACCESSTOKEN';
  public ACCESSTOKENLIFETIMEENDKEY = 'ACCESSTOKENLIFETIMEEND';
  public SIDEBARCOLLAPSED = 'SIDEBARCOLLAPSED';
  public SORTINGPREFERENCE = 'SORTINGPREFERENCE';
  public SHOWABSENCEENTRIESPREFERENCE = 'SHOWABSENCEENTRIESPREFERENCE';
  public userType: UserType;
  public isOfficeUser: boolean = false;
  public isCompanyAdmin: boolean = false;
  public isApplicationAdmin: boolean = false;
  public userTypes = UserType;
  public basicContextMenu: ContextMenuOption[] = [
    { icon: 'create-outline', label: 'Bearbeiten', value: 'edit' },
    { icon: 'trash-outline', label: 'Löschen', value: 'delete' },
  ];
  public supportedPhotoFileTypes: string[] = ['.jpg', '.jpeg', '.png'];
  public supportedVideoFileTypes: string[] = []; //['.mp4', '.mov'];
  public supportedDocumentFileTypes: string[] = ['.pdf'];
  public supportedFileTypes: string[] = [
    ...this.supportedPhotoFileTypes,
    ...this.supportedDocumentFileTypes,
    ...this.supportedVideoFileTypes,
  ];
  public magicNumber: string = '000000000000000000000000';
  public emptyDate: string = '0001-01-01T00:00:00Z';
  public initialized: boolean;
  public updateAvailable: boolean = false;
  private hubConnection!: signalR.HubConnection;
  private dataUpdated$ = new Subject<number>();
  public lastDataUpdatedDate: Date;
  public modt: string;
  private blobStorage: { [id: string]: Blob } = {};
  public refreshBuffer: number = 60000;
  private refreshInProgress: Promise<void> | null = null;
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private previewImageStorage: { [id: string]: Blob } = {};

  init() {
    if (this.initialized) return;
    this.initialized = true;
    console.log('init state');
    this.isBigScreen =
      this.platform.is('desktop') ||
      this.platform.is('tablet') ||
      (!this.platform.platforms().includes('ios') &&
        !this.platform.platforms().includes('android'));

    this.isIOS = this.platform.is('ios');
    if (this.isMobile) {
      this.configService
        .apiV0ConfigurationVersionInfoGet$Json()
        .subscribe((x) => {
          if (x.minVersion > 1 && x.minVersion > this.version) {
            this.toolService.showUpdateAlert();
          } else if (x.latestVersion > this.version) {
            this.toolService.showUpdateAvailableToast();
            this.updateAvailable = true;
          }
        });
    }

    this.configService
      .apiV0ConfigurationMotdGet$Json()
      .subscribe((x) => (this.modt = x));

    this.loadCompanies().subscribe({
      next: (x) => {},
      error: (err) => this.handleError(err),
    });

    this.selectedCompany$
      .pipe(
        switchMap((x) => {
          return x != null
            ? forkJoin([
                //this.loadCalendarEvents(),
                //this.loadCalendars(),
                this.loadConstructionSites(),
                this.loadUsersByCompany(),
                this.loadConstructionDayReports(),
                this.loadWorkingDays(),
                this.loadNodeItems(),
                this.loadAbsenceEntries(),
                this.loadMachines(),
                this.loadInspections(),
                this.loadMissingWorkEntries(),
              ])
            : of([]);
        })
      )
      .subscribe({
        next: (x) => {
          this.lastDataUpdatedDate = new Date();
        },
        error: (err) => this.handleError(err),
      });
    this.parseJwt();
    this.loadRole().subscribe();
    this.startConnection();
    this.setSilentRenew();
  }

  getLastDataUpdates() {
    if (!this.selectedCompany.value) return;
    this.companyService
      .apiV0CompanyGetLastDataUpdatesGet$Json({
        companyId: this.selectedCompany.value.id,
        lastSyncDate: this.lastDataUpdatedDate.toISOString(),
      })
      .subscribe({
        next: (x) => this.handleDataUpdates(x),
        error: (err) => this.handleError(err),
      });
  }

  startConnection() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.base.rootUrl + '/hubs/data-update', {
        accessTokenFactory: () => this.accessToken,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.start().catch((err) => console.error(err));

    this.hubConnection.on('DataUpdated', (data: number) => {
      console.log('Data received from server: ' + data);
      this.dataUpdated$.next(data);
    });

    this.setupDataUpdateProcessing();
  }

  private setupDataUpdateProcessing() {
    this.dataUpdated$
      .pipe(
        bufferTime(500), // collecting 500ms for duplicates
        filter((events) => events.length > 0),
        map((events) => [...new Set(events)])
      )
      .subscribe((events) => {
        this.handleDataUpdates(events);
      });
  }

  private handleDataUpdates(events: number[]) {
    for (const data of events) {
      switch (data) {
        case 0: // AbsenceEntries
          this.loadAbsenceEntries().subscribe();
          this.loadMissingWorkEntries().subscribe();
          break;

        case 1: // NodeItems
          this.loadNodeItems().subscribe();
          break;

        case 2: // CalendarEvents
          // TODO
          break;

        case 3: // ConstructionDayReports
          this.loadConstructionDayReports().subscribe();
          this.loadWorkingDays().subscribe();
          this.loadMissingWorkEntries().subscribe();
          break;

        case 4: // ConstructionSites
          this.loadConstructionSites().subscribe();
          this.loadNodeItems().subscribe();
          break;

        case 5: // Inspections
          this.loadMachines().subscribe();
          this.loadInspections().subscribe();
          break;

        case 6: // Machines
          this.loadMachines().subscribe();
          break;

        case 7: // MissingWorkEntries
          this.loadMissingWorkEntries().subscribe();
          break;

        case 8: // Users
          this.loadUsersByCompany().subscribe();
          break;

        case 9: // WorkEntries
          this.loadWorkingDays().subscribe();
          this.loadConstructionDayReports().subscribe();
          this.loadMissingWorkEntries().subscribe();
          break;

        default:
          console.warn('Unbekanntes DataUpdateHubEvent:', data);
          break;
      }
    }
    this.lastDataUpdatedDate = new Date();
  }

  fullRefresh() {
    this.loadRole().subscribe();
    this.selectedCompany.next(this.selectedCompany.value);
  }

  performHealthCheck() {
    this.configService
      .apiV0ConfigurationAliveGet$Json()
      .subscribe((x) => this.toolService.showInfoToast(x));
  }

  // Companies

  private companies: BehaviorSubject<Company[]> = new BehaviorSubject<
    Company[]
  >([]);
  public companies$: Observable<Company[]> = this.companies.asObservable();

  private selectedCompany: BehaviorSubject<Company> =
    new BehaviorSubject<Company>(null);
  public selectedCompany$: Observable<Company> =
    this.selectedCompany.asObservable();

  public loadCompanies(): Observable<Company[]> {
    return this.companyService.apiV0CompanyGetCompaniesGet$Json().pipe(
      tap((x) => {
        this.companies.next(x);
        if (x?.length > 0 && this.selectedCompany.value == null)
          this.selectedCompany.next(x[0]);
      })
    );
  }

  public changeSelectedCompany(company: Company) {
    this.selectedCompany.next(company);
    this.changeSelectedConSite(null);
  }

  public createCompany(company: Company) {
    this.companyService
      .apiV0CompanyPost({ body: company })
      .pipe(switchMap((x) => this.loadCompanies()))
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('Firma erfolgreich angelegt');
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  public updateCompany(company: Company) {
    /*this.companyService.apiV0CompanyPost({body: company}).subscribe({
        next: (x) => {
          this.toolService.showInfoToast('Firma erfolgreich angelegt');
        },
        error: (err) => {
          this.handleError(err);
        },
      });*/
  }

  // CalendarEvents

  private calendarEvents: BehaviorSubject<CalendarEvent[]> =
    new BehaviorSubject<CalendarEvent[]>([]);
  public calendarEvents$: Observable<CalendarEvent[]> =
    this.calendarEvents.asObservable();

  public loadCalendarEvents(): Observable<any> {
    return this.calendarEventService
      .apiV0CalendareventGetAllCalendarEventsGet$Json({
        companyId: this.selectedCompany.value?.id,
      })
      .pipe(tap((x) => this.calendarEvents.next(x)));
  }

  public createUpdateCalendarEvent(event: CalendarEvent) {
    event.companyId = this.selectedCompany.value.id;
    this.calendarEventService
      .apiV0CalendareventCreateUpdateCalendarEventPost({ body: event })
      .pipe(
        switchMap((x) => {
          let dt = new Date(event.startDate);
          this.toolService.showInfoToast(
            event.name +
              ' am ' +
              this.toolService.formatDt(dt.toDateString()) +
              ' erfolgreich gespeichert!'
          );
          return this.loadCalendars();
        })
      )
      .subscribe();
  }

  refreshCalendarState() {
    this.calendars.next(this.calendars.value);
  }

  public async deleteCalendarEvent(event: CalendarEvent) {
    const confirmed = await this.toolService.showDeleteAlert('dieses Ereignis');
    if (confirmed)
      this.calendarEventService
        .apiV0CalendareventDeleteCalendarEventDelete({
          calendarEventId: event.id,
        })
        .subscribe((x) => {
          this.calendarEvents.value.splice(
            this.calendarEvents.value.indexOf(event),
            1
          );
          this.calendarEvents.next(this.calendarEvents.value);
          this.toolService.showInfoToast('Ereignis erfolgreich gelöscht');
        });
  }

  // Calendar

  private calendars: BehaviorSubject<Calendar[]> = new BehaviorSubject<
    CalendarEvent[]
  >([]);
  public calendars$: Observable<Calendar[]> = this.calendars.asObservable();

  public loadCalendars(): Observable<any> {
    return this.calendarService
      .apiV0CalendarEventTypeCompanyIdCalendarsGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap((x) => {
          this.calendars.next(x);
        })
      );
  }

  public createCalendar(name: string, color: string): Observable<Calendar> {
    let c: Calendar = { name: name, backgroundColorHex: color };
    return this.calendarService
      .apiV0CalendarEventTypeCompanyIdCalendarPost$Json({
        body: c,
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap((x) => {
          this.calendars.value.push(x);
          this.calendars.next(
            this.calendars.value.filter((x) => x.id != 'new')
          );
        })
      );
  }

  // Files

  private selectedConSite: BehaviorSubject<ConstructionSite> =
    new BehaviorSubject<ConstructionSite>(null);
  public selectedConSite$: Observable<ConstructionSite> =
    this.selectedConSite.asObservable();

  public getIcon(doc: NodeItem): string {
    if (this.supportedPhotoFileTypes.includes(doc.fileExtension))
      return 'image-outline';
    if (this.supportedDocumentFileTypes.includes(doc.fileExtension))
      return 'document-outline';
    if (this.supportedVideoFileTypes.includes(doc.fileExtension))
      return 'film-outline';
    return 'folder-outline';
  }

  public changeSelectedConSite(id: string) {
    if (id != null)
      this.selectedConSite.next(this.conSites.value.find((x) => x.id == id));
  }

  public addFolder(name: string, parentId: string) {
    if (!name || !parentId) return;
    let node: NodeItem = {
      name: name,
      parentId: parentId,
      sizeInBytes: 0,
      displayName: name,
      companyId: this.selectedCompany.value.id,
    };
    this.nodeItemService
      .apiV0NodeitemCreateFolderNodeItemPost({ body: node })
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('Ordner erfolgreich angelegt');
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  public renameNodeItem(node: NodeItem) {
    this.nodeItemService
      .apiV0NodeitemRenameNodeItemPost({ body: node })
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('Element erfolgreich umbenannt');
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  public async deleteNodes(
    nodes: string[],
    confirmationRequired: boolean = true
  ) {
    if (!nodes || nodes.length === 0) return;
    let confirmed = false;
    if (confirmationRequired) {
      const msg =
        nodes.length > 1
          ? 'die ausgewählten Elemente'
          : 'das ausgewählte Element';
      confirmed = await this.toolService.showDeleteAlert(msg);
    }

    if (confirmed || !confirmationRequired) {
      this.nodeItemService
        .apiV0NodeitemDeleteDelete({ body: nodes })
        .pipe(
          tap(() => {
            if (!confirmationRequired) {
              const msg =
                nodes.length > 1
                  ? 'Elemente erfolgreich gelöscht'
                  : 'Element erfolgreich gelöscht';
              this.toolService.showInfoToast(msg);
            }
          }),
          catchError((err) => {
            this.handleError(err);

            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  public async moveNodes(nodes: string[], parentNode: string) {
    const confirmed = await this.toolService.showConfirmAlert(
      false,
      'Möchte Sie die ausgewählten Elemente hier her verschieben?',
      'Elemente verschieben'
    );
    if (confirmed) {
      this.nodeItemService
        .apiV0NodeitemMoveNodeItemsPost({ body: nodes, newParent: parentNode })
        .pipe(
          tap(() => this.toolService.showInfoToast('Erfolgreich verschoben')),
          catchError((err) => {
            this.handleError(err);

            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  // Users

  private users: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
  public users$: Observable<User[]> = this.users.asObservable();

  createUser(
    firstName: string,
    lastName: string,
    password: string,
    personalNumber: string,
    type: UserType,
    recievesFixedSalary: boolean
  ) {
    let usr: UserRegistration = {};
    usr.userName = '';
    usr.firstName = firstName;
    usr.lastName = lastName;
    usr.password = password;
    usr.personalNumber = personalNumber;
    usr.companies = [this.selectedCompany.value.id];
    usr.recievesFixedSalary = recievesFixedSalary;
    usr.type = type;
    this.userService.apiV0UserRegisterUserPost$Json({ body: usr }).subscribe({
      next: (x) => {
        this.toolService.showInfoToast(
          'User erfolgreich registriert! Folgender Username wurde zugeordnet: ' +
            x
        );
      },
      error: (err) => this.handleError(err),
    });
  }

  updateUser(user: UserRegistration) {
    this.userService.apiV0UserUpdateUserPost({ body: user }).subscribe({
      next: (x) => {
        this.toolService.showInfoToast('User erfolgreich aktualisiert');
      },
      error: (err) => this.handleError(err),
    });
  }

  deleteUser(username: string) {
    this.userService
      .apiV0UserDeleteUserUsernameDelete({ username: username })
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('User erfolgreich gelöscht');
        },
        error: (err) => this.handleError(err),
      });
  }

  loadUsersByCompany(): Observable<User[]> {
    return this.userService
      .apiV0UserGetUsersByCompanyGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v: User[]) => {
            this.users.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  public getUserDisplayName(userName: string) {
    return this.detectUserDisplayName(userName, this.users.value);
  }

  public getUserDisplayNameAsync(username: string): Observable<string> {
    return this.users$.pipe(
      map((x) => {
        return this.detectUserDisplayName(username, x);
      })
    );
  }

  private detectUserDisplayName(username: string, users: User[]): string {
    let user = users?.find((x) => x.userName == username);
    if (user != null) return user.firstName + ' ' + user.lastName;
    if (username?.length == 24) return 'Bautagesbericht';
    return username;
  }

  deactivateUser(userName: string) {
    this.userService
      .apiV0UserDeactivateUserUsernamePut({ username: userName })
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('User erfolgreich deaktiviert');
        },
        error: (err) => this.handleError(err),
      });
  }

  activateUser(userName: string) {
    this.userService
      .apiV0UserActivateUserUsernamePut({ username: userName })
      .subscribe({
        next: (x) => {
          this.toolService.showInfoToast('User erfolgreich aktiviert');
        },
        error: (err) => this.handleError(err),
      });
  }

  // ConSites

  private conSites: BehaviorSubject<ConstructionSite[]> = new BehaviorSubject<
    ConstructionSite[]
  >(null);
  public conSites$: Observable<ConstructionSite[]> =
    this.conSites.asObservable();

  public loadConstructionSites(): Observable<ConstructionSite[]> {
    return this.conSiteService
      .apiV0ConstructionsiteGetConstructionSitesByCompanyGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.conSites.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  getConstructionSiteName(id: string) {
    const conSite = this.conSites.value?.find((x) => x.id == id);
    return conSite != null ? conSite.name : id;
  }

  getCurrentConstructionSiteName(): string {
    return this.selectedConSite?.value?.name;
  }

  async findConSiteAndEdit(id: string) {
    await this.openConstructionSiteAlert(
      this.conSites.value.find((x) => x.id == id)
    );
  }

  async openConstructionSiteAlert(existing?: ConstructionSite) {
    const isEdit = !!existing;

    const alert = await this.alertController.create({
      header: isEdit ? 'Baustelle bearbeiten' : 'Neue Baustelle',
      message: 'Mit Stern(*) markierte Felder sind Pflichtfelder.',
      inputs: [
        {
          name: 'name',
          type: 'text',
          placeholder: 'Bezeichnung*',
          value: existing?.name || '',
        },
        {
          name: 'constructionSiteNumber',
          type: 'text',
          placeholder: 'Nummer*',
          value: existing?.constructionSiteNumber || '',
        },
        {
          name: 'address',
          type: 'text',
          placeholder: 'Straße + Nr*',
          value: existing?.address || '',
        },
        {
          name: 'postalCode',
          type: 'text',
          placeholder: 'PLZ*',
          value: existing?.postalCode || '',
        },
        {
          name: 'city',
          type: 'text',
          placeholder: 'Ort*',
          value: existing?.city || '',
        },
        {
          name: 'contactName1',
          type: 'text',
          placeholder: 'Kontaktperson 1',
          value: existing?.contactName1 || '',
        },
        {
          name: 'phone1',
          type: 'text',
          placeholder: 'Telefonnummer 1',
          value: existing?.phone1 || '',
        },
        {
          name: 'email1',
          type: 'text',
          placeholder: 'Email 1',
          value: existing?.email1 || '',
        },
        {
          name: 'contactName2',
          type: 'text',
          placeholder: 'Kontaktperson 2',
          value: existing?.contactName2 || '',
        },
        {
          name: 'phone2',
          type: 'text',
          placeholder: 'Telefonnummer 2',
          value: existing?.phone2 || '',
        },
        {
          name: 'email2',
          type: 'text',
          placeholder: 'Email 2',
          value: existing?.email2 || '',
        },
      ],
      buttons: [
        {
          text: 'Abbrechen',
          role: 'cancel',
        },
        {
          text: isEdit ? 'Änderungen speichern' : 'Baustelle erstellen',
          handler: (data) => {
            if (
              !data.name ||
              !data.address ||
              !data.city ||
              !data.postalCode ||
              !data.constructionSiteNumber
            ) {
              return false; // verhindert, dass das Alert automatisch schließt
            }

            if (isEdit) {
              data.id = existing.id;
              data.company = existing.company;
              this.updateConstructionSite(data);
            } else {
              this.createConstructionSite(data);
            }
            return true;
          },
        },
      ],
      cssClass: this.platform.is('desktop') ? 'large-alert' : '',
    });

    await alert.present();
  }

  private createConstructionSite(conSite: ConstructionSite) {
    conSite.id = '';
    conSite.company = this.selectedCompany.value.id;
    this.conSiteService
      .apiV0ConstructionsiteCreateConstructionSitePost({
        body: conSite,
      })
      .subscribe(() => {
        this.toolService.showInfoToast(
          `${conSite.name} erfolgreich hinzugefügt`
        );
      });
  }

  public async deleteConstructionSite(conSiteId: string) {
    const confirmed = await this.toolService.showDeleteAlert(
      'diese Baustelle',
      undefined,
      undefined,
      undefined,
      undefined,
      'Alle Arbeitszeiten, Bautagesberichte, Dokumente und Ordner dieser Baustelle werden gelöscht!'
    );
    if (confirmed) {
      this.conSiteService
        .apiV0ConstructionsiteDeleteConSiteIdDelete({ conSiteId: conSiteId })
        .pipe(
          switchMap(() =>
            forkJoin([
              // todo: delete this here because signal r event will trigger
              this.loadConstructionSites(),
              this.loadConstructionDayReports(),
              this.loadWorkingDays(),
              this.loadNodeItems(),
            ])
          )
        )
        .subscribe({
          next: (x) => {
            this.toolService.showInfoToast('Baustelle erfolgreich gelöscht');
            this.routerService.goBack();
          },
          error: (err) => this.handleError(err),
        });
    }
  }

  private updateConstructionSite(conSite: ConstructionSite) {
    this.conSiteService
      .apiV0ConstructionsiteUpdatePut({ body: conSite })
      .pipe(
        tap(() => {
          this.toolService.showInfoToast(
            `${conSite.name} erfolgreich aktualisiert`
          );
        }),
        catchError((err) => {
          this.handleError(err);

          return EMPTY;
        })
      )
      .subscribe((x) => this.selectedConSite.next(conSite));
  }

  changeConstructionSiteStates(
    conSiteIds: string[],
    state: ConstructionSiteState
  ) {
    this.conSiteService
      .apiV0ConstructionsiteSetConstructionSiteStatePost({
        body: conSiteIds,
        State: state,
      })
      .subscribe({
        next: (v) =>
          this.toolService.showInfoToast(
            'Baustellenstatus erfolgreich geändert!'
          ),
        error: (err) => this.handleError(err),
      });
  }

  // ConstructionDayReports

  private constructionDayReports: BehaviorSubject<ConstructionDayReport[]> =
    new BehaviorSubject<ConstructionDayReport[]>(null);
  public constructionDayReports$: Observable<ConstructionDayReport[]> =
    this.constructionDayReports.asObservable();

  public loadConstructionDayReports(): Observable<ConstructionDayReport[]> {
    return this.constructionDayReportService
      .apiV0ConstructiondayreportGetConstructionDayReportsByCompanyGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.constructionDayReports.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  public createConstructionDayReport(
    report: ConstructionDayReport
  ): Observable<ReportCreationResult> {
    report.companyId = this.selectedCompany.value.id;
    return this.constructionDayReportService.apiV0ConstructiondayreportCreateReportPost$Json(
      { body: report }
    );
  }

  public updateConstructionDayReport(report: ConstructionDayReport) {
    report.companyId = this.selectedCompany.value.id;
    return this.constructionDayReportService.apiV0ConstructiondayreportUpdateConstructionDayReportPost$Json(
      {
        body: report,
      }
    );
  }

  public signConstructionDayReport(id: string) {
    return this.constructionDayReportService
      .apiV0ConstructiondayreportSignConstructionDayReportPost({ reportId: id })
      .subscribe((x) => {
        this.toolService.showInfoToast('Bautagesbericht erfolgreich bestätigt');
      });
  }

  public async deleteReport(report: ConstructionDayReport) {
    const confirmed = await this.toolService.showDeleteAlert(
      'diesen Bautagesbericht'
    );
    if (confirmed)
      this.constructionDayReportService
        .apiV0ConstructiondayreportReportIdDelete({ reportId: report.id })
        .subscribe(async (x) => {
          await this.modalController.dismiss('delete', ModalRole.Confirm);
          this.toolService.showInfoToast(
            'Bautagesbericht erfolgreich gelöscht'
          );
        });
  }

  resolveHours(takeUserHours: boolean, reportId: string) {
    this.constructionDayReportService
      .apiV0ConstructiondayreportCorrectWorkingTimesTakeUserHoursReportIdUserIdPost(
        { reportId: reportId, takeUserHours: takeUserHours, userId: 'test' }
      )
      .subscribe({
        next: (result) => {
          this.toolService.showInfoToast(
            'Bautagesbericht erfolgreich aktualisiert'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  constructionDayReportExists(
    conSite: string,
    dt: string
  ): Observable<Array<WorkingEntry>> {
    return this.constructionDayReportService.apiV0ConstructiondayreportExistingReportInfosPost$Json(
      { body: { date: dt, constructionSite: conSite } }
    );
  }

  loadConSiteReportById(id: string) {
    return this.constructionDayReportService.apiV0ConstructiondayreportGetConstructionDayReportsByIdGet$Json(
      { reportId: id }
    );
  }

  createReportByWorkEntry(
    id: string,
    type: ConstructionDayReportType,
    approve: boolean
  ) {
    this.constructionDayReportService
      .apiV0ConstructiondayreportCreateFromIdWorkingEntryIdReportTypeApprovePost(
        {
          workingEntryId: id,
          reportType: type,
          approve: approve,
        }
      )
      .subscribe({
        next: (result) => {
          this.toolService.showInfoToast(
            'Arbeitszeitbericht erfolgreich erstellt'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  public reportHasAttachments(id: string): Observable<boolean> {
    return this.nodeItems$.pipe(
      map((x) => x.find((y) => y.constructionDayReportId == id) != null)
    );
  }

  // AbsenceEntries

  private absenceEntries: BehaviorSubject<AbsenceEntry[]> = new BehaviorSubject<
    AbsenceEntry[]
  >([]);
  public absenceEntries$: Observable<AbsenceEntry[]> =
    this.absenceEntries.asObservable();

  loadAbsenceEntries(): Observable<AbsenceEntry[]> {
    return this.absenceEntryService
      .apiV0AbsenceEntryGetAllAbsenceEntriesByCompanyGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.absenceEntries.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  sendAbsenceEntryRequest(request: AbsenceEntryRequest) {
    this.absenceEntryService
      .apiV0AbsenceEntryCreateMultiplePost({ body: request })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast(
            'Abwesenheitsantrag erfolgreich angelegt!'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  updateAbsenceEntry(entry: AbsenceEntry) {
    this.absenceEntryService
      .apiV0AbsenceEntryUpdateAbsenceEntryPost({ body: entry })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast(
            'Abwesenheitsantrag erfolgreich aktualisiert!'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  approveAbsenceEntry(id: string) {
    this.absenceEntryService
      .apiV0AbsenceEntryApproveAbsenceEntryPost({ id: id })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast(
            'Abwesenheitsantrag erfolgreich bestätigt!'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  rejectAbsenceEntry(id: string, message: string) {
    this.absenceEntryService
      .apiV0AbsenceEntryRejectAbsenceEntryPost({ id: id, message: message })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast(
            'Abwesenheitsantrag erfolgreich abgelehnt!'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }
  async deleteMultipleAbsenceEntry(ids: string[]): Promise<boolean> {
    const text =
      ids.length > 1
        ? 'diese ' + ids.length + ' Abwesenheiten'
        : 'diese Abwesenheit';
    const confirmed = await this.toolService.showDeleteAlert(text);
    if (confirmed) {
      this.absenceEntryService
        .apiV0AbsenceEntryDeleteMultipleDelete({ body: ids })
        .subscribe({
          next: () => {
            this.toolService.showInfoToast('Erfolgreich gelöscht!');
          },
          error: (err) => {
            this.handleError(err);
          },
        });
    }
    return confirmed;
  }

  async deleteAbsenceEntry(id: string): Promise<void> {
    const confirmed = await this.toolService.showDeleteAlert(
      'diesen Abwesenheitsantrag'
    );
    if (confirmed) {
      this.absenceEntryService
        .apiV0AbsenceEntryDeleteByIdDelete({ id: id })
        .subscribe({
          next: () => {
            this.toolService.showInfoToast(
              'Abwesenheitsantrag erfolgreich gelöscht!'
            );
          },
          error: (err) => {
            this.handleError(err);
          },
        });
    }
  }

  // WorkingDays

  private workingDays: BehaviorSubject<WorkDay[]> = new BehaviorSubject<
    WorkDay[]
  >([]);
  public workingDays$: Observable<WorkDay[]> = this.workingDays.asObservable();

  calculateWorkDayMinutes(timeEntries: TimeEntry[]): number {
    let allEntryMinutes = 0;
    timeEntries.forEach((x) => {
      let start = new Date(x.start);
      let end = new Date(x.end);
      let hoursDiff = end.getHours() - start.getHours();
      const milliDiff: number = end.getTime() - start.getTime();
      const totalSeconds = Math.floor(milliDiff / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      allEntryMinutes += totalMinutes;
    });
    return allEntryMinutes;
  }

  addWorkDay(wd: WorkDay, workers: string[]) {
    let subs: Observable<void>[] = [];
    workers.forEach((x) => {
      wd.user = x;
      subs.push(
        this.workDayService.apiV0WorkDayCreateWorkDayPost({
          body: wd,
        })
      );
    });
    forkJoin(subs).subscribe({
      next: () => {
        this.toolService.showInfoToast('Arbeitszeit erfolgreich angelegt!');
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  updateWorkDay(wd: WorkDay) {
    this.workDayService.apiV0WorkDayUpdateWorkDayPost({ body: wd }).subscribe({
      next: () => {
        this.toolService.showInfoToast('Arbeitszeit erfolgreich aktualisiert!');
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  loadWorkingDays() {
    return this.workDayService
      .apiV0WorkDayGetAllWorkingDaysByCompanyGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.workingDays.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  async deleteWorkDay(wd: WorkDay) {
    const confirmed = await this.toolService.showDeleteAlert(
      'diesen Arbeitstag'
    );
    if (confirmed) {
      let obs: Observable<void>[] = [];
      wd.workEntryIds.forEach((x) => {
        obs.push(this.workDayService.apiV0WorkDayDeleteByIdDelete({ id: x }));
      });
      forkJoin(obs).subscribe((x) => {
        this.toolService.showInfoToast('Arbeitstag erfolgreich gelöscht');
      });
    }
  }

  public buildHourOverview(req: WorkingOverviewRequest) {
    if (req.users.length == 0)
      req.users = this.users.value.map((x) => x.userName);
    req.companyId = this.selectedCompany.value.id;
    if (req.type == 'export') {
      this.binaryService
        .apiV0BinaryHourOverviewExportPost$Json({ body: req })
        .subscribe((x) => {
          const url = window.URL.createObjectURL(x);

          const a = document.createElement('a');
          a.href = url;
          a.download = 'IMP_LBW.TXT';
          a.style.display = 'none';

          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        });
    } else {
      this.openFile(btoa(JSON.stringify(req)), 'hour-overview');
    }
  }

  public getExistingWorkDays(
    workDayId: string,
    usernames: string[],
    date: string
  ): string[] {
    let usersWithExistingWorkDays: string[] = [];
    usernames.forEach((username) => {
      let entry = this.workingDays.value.find(
        (x) =>
          x.id != workDayId &&
          x.user == username &&
          this.toolService.dateStringsAreSameDay(x.workingDate, date)
      );
      if (entry != null) {
        usersWithExistingWorkDays.push(username);
      }
    });
    return usersWithExistingWorkDays;
  }

  // NodeItems

  private nodeItems: BehaviorSubject<NodeItem[]> = new BehaviorSubject<
    NodeItem[]
  >([]);
  public nodeItems$: Observable<NodeItem[]> = this.nodeItems.asObservable();

  loadNodeItem(id: string): NodeItem {
    return this.nodeItems.value.find((x) => x.id == id);
  }

  public calculateStorageSize(): string {
    let sum = 0;
    this.nodeItems.value.forEach((x) => {
      sum += x.sizeInBytes;
    });
    return (sum / 1000000000).toFixed(1);
  }

  loadNodeItems(): Observable<NodeItem[]> {
    return this.nodeItemService
      .apiV0NodeitemGetbycompanyidGet$Json({
        companyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.nodeItems.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }
  async openFile(id: any, type: string, openInModal: boolean = false) {
    if (this.isMobile && !openInModal) {
      if (type == 'document') {
        this.routerService.routeToDocumentViewer(id);
      } else if (type == 'hour-overview') {
        this.routerService.routeToHourOverviewViewer(id);
      } else if (type == 'construction-day-report') {
        this.routerService.routeToConstructionDayReportViewer(id);
      } else if (type == 'inspection') {
        this.routerService.routeToInspectionViewer(id);
      }
      return;
    }
    const modal = await this.modalController.create({
      component: ViewerDialogComponent,
      componentProps: {
        docId: id,
        pdfType: type,
      },
      cssClass: this.isBigScreen ? 'viewer-modal' : '',
    });
    modal.present();
  }

  pushToBlobStorage(id: string, blob: Blob) {
    this.blobStorage[id] = blob;
  }

  pushToPreviewImageStorage(id: string, blob: Blob) {
    this.previewImageStorage[id] = blob;
  }

  downloadPreviewImage(id: string) {
    if (this.previewImageStorage[id] != null) {
      return of(this.previewImageStorage[id]);
    } else {
      return this.binaryService
        .apiV0BinaryDocumentPreviewImageIdGet$Json({
          id: id,
        })
        .pipe(tap((x) => (this.previewImageStorage[id] = x)));
    }
  }

  downloadBinary(id: string, pdfType: string): Observable<Blob> {
    if (pdfType == 'construction-day-report')
      return this.binaryService.apiV0BinaryConstructionDayReportContentIdGet$Json(
        { id: id }
      );
    if (pdfType == 'document') {
      if (this.blobStorage[id] != null) {
        return of(this.blobStorage[id]);
      } else {
        return this.binaryService
          .apiV0BinaryDocumentContentIdGet$Json({
            id: id,
          })
          .pipe(tap((x) => (this.blobStorage[id] = x)));
      }
    }
    if (pdfType == 'inspection')
      return this.binaryService.apiV0BinaryInspectionContentGet$Json({
        id: id,
      });

    const req: WorkingOverviewRequest = JSON.parse(atob(id));
    return this.binaryService.apiV0BinaryHourOverviewContentPost$Json({
      body: req,
    });
  }

  uploadFile(req: UploadDocumentRequest): Observable<void> {
    req.companyId = this.selectedCompany.value.id;
    return this.binaryService.apiV0BinaryUploadDocumentsPost({
      body: req as any,
    });
  }

  // Machines

  private machines: BehaviorSubject<Machine[]> = new BehaviorSubject<Machine[]>(
    []
  );
  public machines$: Observable<Machine[]> = this.machines.asObservable();

  loadMachines(): Observable<Machine[]> {
    return this.machineService
      .apiV0MachineGetByCompanyGet$Json({
        CompanyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.machines.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  createMachine(entry: Machine) {
    entry.companyId = this.selectedCompany.value.id;
    this.machineService.apiV0MachineCreatePost({ body: entry }).subscribe({
      next: () => {
        this.toolService.showInfoToast('Maschine erfolgreich angelegt!');
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  updateMachine(entry: Machine) {
    this.machineService.apiV0MachineUpdatePost({ body: entry }).subscribe({
      next: () => {
        this.toolService.showInfoToast('Maschine erfolgreich aktualisiert!');
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  async deleteMachine(id: string) {
    const confirmed = await this.toolService.showDeleteAlert(
      'diese Maschine',
      null,
      null,
      null,
      null,
      'Dabei werden auch alle DGUV-Prüfungen und Dokumente der Maschine gelöscht!'
    );
    if (confirmed) {
      this.machineService.apiV0MachineDeleteByIdDelete({ id: id }).subscribe({
        next: () => {
          this.toolService.showInfoToast(
            'Maschine, Prüfungen und Dokumente erfolgreich gelöscht!'
          );
        },
        error: (err) => {
          this.handleError(err);
        },
      });
    }
  }

  public getMachineNameAsync(id: string): Observable<string> {
    return this.machines$.pipe(
      map((x) => {
        let machine = x.find((y) => y.id == id);
        return machine == null
          ? id
          : `${machine.inventoryNumber} ${machine.manufacturer} ${machine.type}`;
      })
    );
  }

  // Inspections

  private inspections: BehaviorSubject<Inspection[]> = new BehaviorSubject<
    Inspection[]
  >([]);
  public inspections$: Observable<Inspection[]> =
    this.inspections.asObservable();

  loadInspections(): Observable<Inspection[]> {
    return this.inspectionService
      .apiV0InspectionGetByCompanyGet$Json({
        CompanyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.inspections.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  createInspection(entry: Inspection) {
    entry.companyId = this.selectedCompany.value.id;
    this.inspectionService
      .apiV0InspectionCreatePost({ body: entry })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast('Prüfung erfolgreich angelegt!');
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  updateInspection(entry: Inspection) {
    this.inspectionService
      .apiV0InspectionUpdatePost({ body: entry })
      .subscribe({
        next: () => {
          this.toolService.showInfoToast('Prüfung erfolgreich aktualisiert!');
        },
        error: (err) => {
          this.handleError(err);
        },
      });
  }

  async deleteInspection(id: string) {
    const confirmed = await this.toolService.showDeleteAlert('diese Prüfung');
    if (confirmed) {
      this.inspectionService
        .apiV0InspectionDeleteByIdDelete({ id: id })
        .subscribe({
          next: () => {
            this.toolService.showInfoToast('Prüfung erfolgreich gelöscht!');
          },
          error: (err) => {
            this.handleError(err);
          },
        });
    }
  }

  approveInspection(id: string) {
    this.inspectionService.apiV0InspectionApprovePost({ id: id }).subscribe({
      next: () => {
        this.toolService.showInfoToast('UVV-Prüfung erfolgreich bestätigt!');
      },
      error: (err) => {
        this.handleError(err);
      },
    });
  }

  // Missing Work Entries

  private missingWorkEntries: BehaviorSubject<MissingWorkEntry[]> =
    new BehaviorSubject<MissingWorkEntry[]>([]);
  public missingWorkEntries$: Observable<MissingWorkEntry[]> =
    this.missingWorkEntries.asObservable();

  loadMissingWorkEntries(): Observable<MissingWorkEntry[]> {
    return this.missingWorkEntryService
      .apiV0MissingworkentryGetByCompanyGet$Json({
        CompanyId: this.selectedCompany.value.id,
      })
      .pipe(
        tap({
          next: (v) => {
            this.missingWorkEntries.next(v);
          },
          error: (err) => this.handleError(err),
        })
      );
  }

  async deleteMissingWorkEntries(ids: string[]) {
    const text =
      ids.length > 1 ? 'diese ' + ids.length + ' Einträge' : 'diesen Eintrag';
    const confirmed = await this.toolService.showDeleteAlert(text);
    if (confirmed) {
      this.missingWorkEntryService
        .apiV0MissingworkentryDeleteByIdsDelete({ body: ids })
        .subscribe({
          next: () => {
            this.toolService.showInfoToast('Erfolgreich gelöscht!');
          },
          error: (err) => {
            this.handleError(err);
          },
        });
    }
  }

  // Error Handling

  public handleError(err: HttpErrorResponse) {
    let message = '';
    if (err?.status === 0) {
      message = 'Keine Internetverbindung vorhanden!';
    } else if (this.userHasMinType(UserType.$2)) {
      message = err.status + ' ' + err.message;
      this.toolService.sendErrorToServer(err, this.userName, this.version);
    } else {
      this.toolService.sendErrorToServer(err, this.userName, this.version);
      message = 'Hoppla, hier ist etwas schiefgelaufen';
    }
    this.toolService.showErrorToast(message);
  }

  // Auth stuff

  accessToken: string;
  refreshToken: string;
  accessTokenLifetimeEnd: Date;
  userName: string;
  displayName: string;
  firstName: string;

  authenticate(username: string, password: string): Observable<AuthResult> {
    return this.authService.apiV0AuthLoginPost$Json({
      body: {
        username: username,
        password: password,
        version: this.version.toString(),
        platform: this.getPlatform(),
      },
    });
  }

  async ensureTokenIsValid(): Promise<void> {
    console.log('EnsureTokenIsValid');
    if (this.refreshInProgress) {
      console.log('Refresh already in progress. Return!');
      return this.refreshInProgress;
    }

    if (!this.initialized) {
      console.log('App just stared, reading everything from storage');
      this.accessToken = await this.readFromSecureStorage(this.ACCESSTOKENKEY);
      const storedDate = await this.readFromSecureStorage(
        this.ACCESSTOKENLIFETIMEENDKEY
      );
      if (storedDate) {
        this.accessTokenLifetimeEnd = new Date(storedDate);
      } else {
        this.accessTokenLifetimeEnd = null;
      }
    }

    if (
      this.accessTokenLifetimeEnd &&
      this.accessToken &&
      this.accessTokenLifetimeEnd.getTime() - this.refreshBuffer > Date.now()
    ) {
      console.log('Access token is still valid');
      if (this.initialized) {
        this.getLastDataUpdates();
      } else {
        this.init();
      }
    } else {
      console.log('Token oudated, refresh!');
      const refreshToken =
        this.refreshToken ??
        (await this.readFromSecureStorage(this.REFRESHTOKENKEY));
      if (refreshToken || !this.isMobile) {
        console.log('start refreshing');
        try {
          this.refreshInProgress = this.refreshAccessToken(refreshToken);
          await this.refreshInProgress;
        } finally {
          this.refreshInProgress = null;
        }
      } else {
        console.log('navigating to login');
        if (window.location.pathname != '/login') {
          this.router.navigate(['/login']);
        }
      }
    }
  }

  async refreshAccessToken(refreshToken: string) {
    // errorhandling needed if this fails
    refreshToken = refreshToken ?? '';
    const result = await firstValueFrom(
      this.authService.apiV0AuthRefreshPost$Json({
        body: {
          refreshToken: refreshToken,
          version: this.version.toString(),
          platform: this.getPlatform(),
        },
      })
    );
    await this.processAuthResult(result);
  }

  async processAuthResult(authResult: AuthResult) {
    console.log('Result from backend:');
    console.log(authResult);
    if (authResult == null) {
      this.router.navigate(['/login']);
      return;
    }
    this.accessToken = authResult.accessToken;
    this.refreshToken = authResult.refreshToken;
    await this.saveToSecureStorage(this.ACCESSTOKENKEY, this.accessToken);
    await this.saveToSecureStorage(this.REFRESHTOKENKEY, this.refreshToken);
    let obj = this.parseJwt();
    this.accessTokenLifetimeEnd = new Date(obj.exp * 1000);
    await this.saveToSecureStorage(
      this.ACCESSTOKENLIFETIMEENDKEY,
      this.accessTokenLifetimeEnd.toISOString()
    );
    this.setSilentRenew();
    console.log(window.location.pathname);
    if (window.location.pathname == '/login') {
      this.routerService.routeToHome();
    }
  }

  private setSilentRenew() {
    if (this.accessTokenLifetimeEnd == null) return;
    const expiresInMs = this.accessTokenLifetimeEnd.getTime() - Date.now();
    const delay = Math.max(0, expiresInMs - this.refreshBuffer);
    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(async () => {
      console.log('Performing silent renew');
      await this.ensureTokenIsValid();
    }, delay);
  }

  public async logout() {
    const confirmed = await this.toolService.showConfirmAlert(
      false,
      'Möchtest du dich sicher abmelden?',
      'Abmelden'
    );
    if (confirmed) {
      await Preferences.clear();
      await SecureStoragePlugin.clear();
      this.accessToken = null;
      this.accessTokenLifetimeEnd = null;
      this.userType = this.userTypes.$0;
      this.authService
        .apiV0AuthLogoutPost({ refreshToken: this.refreshToken })
        .subscribe({
          next: () => {
            this.refreshToken = null;
            window.location.reload();
          },
          error: (err) => {
            this.handleError(err);
          },
        });
    }
  }

  parseJwt(): any {
    var base64Url = this.accessToken.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    let obj = JSON.parse(jsonPayload);
    this.userName = obj.name;
    this.firstName = obj.given_name;
    this.displayName = obj.given_name + ' ' + obj.family_name;
    return obj;
  }

  public userHasMinType(type: UserType): boolean {
    return this.userType >= type;
  }

  currentSignature: string;

  loadSignature() {
    this.userService
      .apiV0UserGetUserSignatureGet$Json()
      .subscribe((x) => (this.currentSignature = x));
  }

  saveSignature(sig: string) {
    this.userService
      .apiV0UserSaveUserSignaturePost({ body: { signature: sig } })
      .subscribe((x) => {
        this.toolService.showInfoToast('Unterschrift erfolgreich gespeichert!');
        this.currentSignature = sig;
      });
  }

  private usrType: BehaviorSubject<UserType> = new BehaviorSubject<UserType>(
    UserType.$0
  );
  public usrType$: Observable<UserType> = this.usrType.asObservable();

  public loadRole(): Observable<UserType> {
    return this.userService.apiV0UserGetUserTypeGet$Json().pipe(
      tap((x) => {
        this.userType = x;
        this.usrType.next(x);
        if (x >= UserType.$1) this.isOfficeUser = true;
        if (x >= UserType.$2) this.isCompanyAdmin = true;
        if (x >= UserType.$3) this.isApplicationAdmin = true;
      })
    );
  }

  async openPopover(
    ev: any,
    addBasicOptions: boolean,
    options?: ContextMenuOption[]
  ): Promise<any> {
    let opts = options ?? [];

    if (this.userHasMinType(UserType.$1) && addBasicOptions) {
      opts = [...opts, ...this.basicContextMenu]; // immutabel statt push
    }

    const popover = await this.popoverController.create({
      component: ContextMenuComponent,
      event: ev,
      translucent: true,
      componentProps: {
        contextMenuOptions: opts,
      },
    });

    await popover.present();

    return await popover.onDidDismiss();
  }

  getPlatform(): string {
    if (this.platform.is('ios')) return 'ios';
    if (this.platform.is('android')) return 'android';
    return 'desktop';
  }

  // Secure Storage

  async saveToSecureStorage(key: string, value: string) {
    await SecureStoragePlugin.set({
      key: key,
      value: value,
    });
  }
  async readFromSecureStorage(key: string): Promise<string | null> {
    try {
      return (await SecureStoragePlugin.get({ key: key })).value;
    } catch (error) {
      return null;
    }
  }

  // Modals --> extracting in a separate service?

  async openConstructionDayReportDetailModal(id: string) {
    const modal = await this.modalController.create({
      component: ConstructionDayReportDetailComponent,
      componentProps: {
        paramId: id,
      },
      cssClass: this.isBigScreen ? 'custom-desktop-modal' : '',
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    switch (role) {
      case ModalRole.Edit:
        this.openConstructionDayReportFormModal(data);
        break;
      case ModalRole.View:
        await this.openFile(data, 'construction-day-report');
        break;
    }
  }

  async openConstructionDayReportFormModal(id: string) {
    const modal = await this.modalController.create({
      component: ConstructionDayReportFormComponent,
      componentProps: {
        reportId: id,
      },
      cssClass: this.isBigScreen ? 'custom-desktop-modal' : '',
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm' && id != null)
      this.openConstructionDayReportDetailModal(id);
  }
}
